// Import the rules context
import { useRulesDetailModalContext } from 'contexts/RulesDetailModalContext';
import { createContext, useCallback, useContext } from 'react';

import { type Rule, useRegenerateRequirementsReview } from '@frontend/api';
import { useToast } from '@frontend/components';

// Simplified context for PDF viewer - just provides openModal function
type PDFViewerRulesContextType = {
  openModal: (selectedText?: string) => void;
};

const PDFViewerRulesContext = createContext<PDFViewerRulesContextType | null>(
  null,
);

export const usePDFViewerRulesContext = () => {
  const context = useContext(PDFViewerRulesContext);
  if (!context) {
    throw new Error(
      'usePDFViewerRulesContext must be used within a PDFViewerRulesProvider',
    );
  }
  return context;
};

type PDFViewerRulesProviderProps = {
  children: React.ReactNode;
  quoteItemId?: number;
};

export const PDFViewerRulesProvider = ({
  children,
  quoteItemId,
}: PDFViewerRulesProviderProps) => {
  const { openModal: globalOpenModal } = useRulesDetailModalContext();
  const toast = useToast();

  // Use direct API call for regeneration
  const { mutate: regenerateReqsReview } = useRegenerateRequirementsReview({
    queryOptions: {
      onError: () => {
        toast({
          title: 'Failed to reload rules',
          status: 'error',
        });
      },
    },
  });

  const onRuleCreateSuccess = useCallback(
    (data: Rule) => {
      toast({
        title: 'Created Rule',
        description: `Rule "${data.name}" created successfully`,
        status: 'success',
      });
      // Trigger backend regeneration for autorefresh
      if (quoteItemId) {
        regenerateReqsReview({ quoteItemId: String(quoteItemId) });
      }
    },
    [toast, regenerateReqsReview, quoteItemId],
  );

  const openModal = useCallback(
    (selectedText?: string) => {
      globalOpenModal({
        selectedText,
        onSuccess: onRuleCreateSuccess,
        mode: 'create',
      });
    },
    [globalOpenModal, onRuleCreateSuccess],
  );

  return (
    <PDFViewerRulesContext.Provider value={{ openModal }}>
      {children}
    </PDFViewerRulesContext.Provider>
  );
};
